// JX-LOOKUP-001 (Oct 2026): shared sub-jurisdiction directory lookup for the
// Fulfillment selectors. Restores the records PJR-001 r4 dropped when the
// state-wide Tier-2 fallback was removed — directory rows with NO parent county
// (New England towns, legacy imports) — WITHOUT re-offering another county's
// rows. Also treats Town <-> Township as lookup siblings.

const SIBLING_TYPES = { town: ['township'], township: ['town'] };

export const lookupTypesFor = (type) => {
  const t = (type || '').toLowerCase();
  return [t, ...(SIBLING_TYPES[t] || [])];
};

export const normalizeCountyName = (value) =>
  String(value || '').trim().toUpperCase().replace(/\s+(COUNTY|PARISH)$/, '');

export const isUnparentedOrSameCounty = (record, county) => {
  const parent = record?.parent_county_name;
  if (!parent || parent === 'None') return true;
  if (!county) return true;
  const a = normalizeCountyName(parent).split(/\s+/);
  const b = normalizeCountyName(county).split(/\s+/);
  // token-prefix match either way: "MIDDLESEX" ↔ "MIDDLESEX SOUTH" (MA registry districts), never "ORANGE" ↔ "ORANGEBURG"
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.every((tok, i) => tok === long[i]);
};

const fetchList = async (params, headers) => {
  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jurisdictions?${params.toString()}`, { headers });
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.jurisdictions || [];
};

const buildParams = (type, state, county, pageSize) => {
  const p = new URLSearchParams({ jurisdiction_type: type, active_only: 'true', page_size: String(pageSize) });
  if (state) p.append('state', state);
  if (county) p.append('parent_county_name', county);
  return p;
};

/**
 * Tier 1: server-side parent_county_name match (unchanged behaviour).
 * Tier 2: when a county filter was applied — state-scoped fetch, kept client-side to
 *         records with NO parent county or the same county, unioned with Tier 1.
 *         Never offers another county's rows.
 * Results are state-filtered, de-duplicated and sorted by name.
 */
export const fetchSubJurisdictions = async ({ type, state, county, useCountyFilter = true }) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const normalizedState = (state || '').trim().toUpperCase();
  const countyFilter = useCountyFilter ? (county || '').trim() : '';
  const types = lookupTypesFor(type);

  let items = [];
  for (const t of types) {
    items.push(...(await fetchList(buildParams(t, normalizedState, countyFilter, 500), headers)));
  }
  const tier1Count = items.length;

  if (countyFilter) {
    for (const t of types) {
      const stateWide = await fetchList(buildParams(t, normalizedState, '', 1000), headers);
      items.push(...stateWide.filter((j) => isUnparentedOrSameCounty(j, countyFilter)));
    }
  }

  if (normalizedState) {
    items = items.filter((j) => (j.state || '').toUpperCase() === normalizedState);
  }
  const seen = new Set();
  items = items.filter((j) => (seen.has(j.id) ? false : (seen.add(j.id), true)));
  items.sort((a, b) => (a.jurisdiction_name || '').localeCompare(b.jurisdiction_name || ''));
  return { items, tier: tier1Count > 0 ? 'county' : 'unparented' };
};
